import { useEffect, useRef, useState } from 'react';
import { mdHtml } from '../logik/md.js';
import { MdHelp, MdLeiste, mdTaste } from './fields';
import { Icon } from './Icon';

/** Formatierter Text (Markdown) nur zur Anzeige. */
export function MdText({ text, testid, klasse }: { text: string; testid?: string; klasse?: string }) {
  return <div className={'md' + (klasse ? ' ' + klasse : '')} data-testid={testid} dangerouslySetInnerHTML={{ __html: mdHtml(text) }} />;
}

/**
 * Längerer Text mit Formatierung (Markdown): Angezeigt wird der formatierte Text; ein Klick darauf oder „Bearbeiten“ öffnet
 * das Textfeld, Verlassen des Feldes speichert und zeigt wieder den formatierten Text, Esc verwirft die Änderung.
 * <ul>
 * <li>nurLesen: nur der formatierte Text (ohne Inhalt „–“).</li>
 * <li>sofort: jede Eingabe wird gemeldet (für Formulare mit eigenem „Speichern“); ein leeres Feld ist dann gleich ein Textfeld,
 *   und ein geöffnetes bleibt bis zum Schließen des Formulars offen (Esc stellt den Text von vorher wieder her).</li>
 * </ul>
 * Kennungen für Tests: `${testid}-feld`, `${testid}-bearbeiten`, `${testid}` (Textfeld), `${testid}-text` (Anzeige).
 */
export function MdFeld({ label, wert, onCommit, testid, platzhalter, leer, nurLesen, sofort, hoch }: {
  label: string; wert: string; onCommit: (v: string) => void; testid: string;
  /** Platzhalter im Textfeld */
  platzhalter?: string;
  /** Text der leeren Anzeige */
  leer?: string;
  nurLesen?: boolean; sofort?: boolean;
  /** hohes Textfeld (für Haupttexte) */
  hoch?: boolean;
}) {
  const [bearbeiten, setBearbeiten] = useState(false);
  const [text, setText] = useState(wert);
  const abbruch = useRef(false); // Esc: das anschließende Verlassen des Feldes speichert nicht
  const feld = useRef<HTMLTextAreaElement>(null);
  const schreiben = (v: string) => { setText(v); if (sofort) onCommit(v); };
  const vorher = useRef(wert);   // Stand beim Öffnen – dahin führt Esc zurück, auch wenn „sofort“ schon gemeldet hat
  useEffect(() => { if (!bearbeiten) setText(wert); }, [wert, bearbeiten]);
  const offen = !nurLesen && (bearbeiten || (!!sofort && !wert.trim()));
  const fertig = () => {
    // In Formularen bleibt das Textfeld offen: Würde es beim Verlassen zur Anzeige, verschöbe sich der Dialog genau in dem
    // Moment, in dem man auf „Speichern“ klickt – der Klick ginge daneben
    if (sofort) return;
    setBearbeiten(false);
    if (abbruch.current) { abbruch.current = false; return; }
    if (text !== wert) onCommit(text);
  };
  const auf = () => { abbruch.current = false; vorher.current = wert; setText(wert); setBearbeiten(true); };
  const zurueck = () => {
    abbruch.current = true;
    if (sofort && vorher.current !== wert) onCommit(vorher.current);
    setText(vorher.current);
    setBearbeiten(false);
  };
  return (
    <div className="field md-feld" data-testid={testid + '-feld'}>
      <span>{label}
        {!nurLesen && !offen && <button type="button" className="btn small" onClick={auf} data-testid={testid + '-bearbeiten'}><Icon name="edit" /> Bearbeiten</button>}
      </span>
      {offen ? (
        <>
          <MdLeiste feld={feld} setzen={schreiben} testid={testid} />
          <textarea ref={feld} className={hoch ? 'tall' : ''} autoFocus={bearbeiten} value={text} placeholder={platzhalter} data-testid={testid}
            onFocus={() => { if (!bearbeiten) { vorher.current = wert; abbruch.current = false; setBearbeiten(true); } }}
            onChange={(e) => schreiben(e.target.value)} onBlur={fertig}
            onKeyDown={(e) => { if (mdTaste(e, feld, schreiben)) return; if (e.key === 'Escape' && bearbeiten && (text !== vorher.current || !sofort || vorher.current.trim())) { e.stopPropagation(); zurueck(); } }} />
          {/* Formatierungshilfe aufklappen, ohne dass das Textfeld den Fokus verliert (das würde speichern und schließen) */}
          <div onMouseDown={(e) => e.preventDefault()}><MdHelp /></div>
        </>
      ) : wert.trim() ? (
        nurLesen
          ? <MdText text={wert} testid={testid + '-text'} klasse="md-anzeige nur-lesen" />
          // Links im Text öffnen, statt das Feld zu bearbeiten
          : <div className="md md-anzeige" role="button" tabIndex={0} title="Klicken zum Bearbeiten" data-testid={testid + '-text'}
              onClick={(e) => { if (!(e.target as HTMLElement).closest('a')) auf(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) auf(); }}
              dangerouslySetInnerHTML={{ __html: mdHtml(wert) }} />
      ) : nurLesen ? (
        <div className="md-anzeige nur-lesen leer" data-testid={testid + '-text'}>–</div>
      ) : (
        <button type="button" className="md-anzeige leer" onClick={auf} data-testid={testid + '-text'}>{leer ?? 'Klicken, um Text zu schreiben …'}</button>
      )}
    </div>
  );
}
